import ReactDOM from "react-dom/client";
import axios from "axios";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Helmet } from "react-helmet";
import { AppRoutes } from "../components/routes";
import { QueryClient, QueryClientProvider } from "react-query";
import { Slide } from "react-toastify";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Dropdown } from "react-bootstrap";
import { AppProvider } from "../contexts/AppContext";
import { initDevCorner } from "../utils/dev_corner";
import { useApiMutation } from "../utils/api";
import { ModalProvider } from "../utils/modal_launcher";

const csrfToken = (document.querySelector("meta[name=csrf-token]") as HTMLMetaElement)
  ?.content;
axios.defaults.headers.common["X-CSRF-Token"] = csrfToken;
axios.defaults.headers.common["Accept"] = "application/json";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 0,
    },
  },
});

initDevCorner();

const inferTitle = (path, title) => {
  if (title) {
    return `IdeaBoard - ${title}`;
  } else {
    return "IdeaBoard";
  }
};

const NotFound = () => {
  return (
    <div className="d-flex flex-column align-items-center min-vh-100 bg-light pt-20">
      <h1 className="display-1 mb-4">Not Found</h1>
      <p className="text-muted mb-4">
        The page you are looking for might have been removed or is temporarily
        unavailable.
      </p>
      <a href="/" className="btn btn-primary">
        Return Home
      </a>
    </div>
  );
};

const App = () => {
  return (
    <ModalProvider>
      <AppProvider>
        <Router>
          <Routes>
            {AppRoutes.map(([path, Component, title]) => (
              <Route
                key={path as string}
                path={path as string}
                Component={(props) => (
                  <>
                    <Helmet>
                      <title>{inferTitle(path, title)}</title>
                    </Helmet>
                    <Component {...props} />
                  </>
                )}
              />
            ))}
            <Route
              path="*"
              Component={() => {
                return <NotFound />;
              }}
            />
          </Routes>
        </Router>

        <ToastContainer
          position="bottom-center"
          hideProgressBar
          autoClose={1750}
          transition={Slide}
        />
      </AppProvider>
    </ModalProvider>
  );
};

ReactDOM.createRoot(document.getElementById("root")!).render(
  <QueryClientProvider client={queryClient}>
    <App />
  </QueryClientProvider>
);

const TopBarDropdown = () => {
  const { post } = useApiMutation();
  const theme = (document.getElementById("top-bar-dropdown") as HTMLDivElement)?.dataset
    .theme;

  const userAttrs = (window as any).userAttrs;

  const app = location.pathname.split("/")[1];

  const groups: Record<string, [string, any, string?][]> = {
    apps: [],
    user: [],
    logout: [],
  };

  if (app === "admin") {
    groups.apps.push(["User App", "/"]);
  } else {
    groups.user.push(["Account", "/account"]);
  }

  if (userAttrs.revert) {
    groups.apps.push([
      "Back to Admin",
      () => post(`/api/common/become/revert`).then(() => (window.location.href = "/")),
    ]);
  }
  if (userAttrs.is_admin && !location.pathname.startsWith("/admin")) {
    groups.apps.push(["Admin", "/admin"]);
  }

  groups.logout.push([
    "Logout",
    () => post("/api/common/logout").then(() => (window.location.href = "/")),
    "red",
  ]);

  return (
    <>
      <Dropdown>
        <Dropdown.Toggle
          variant={theme || ""}
          className="btn-text"
          name="topbar-user-menu"
        >
          <span className="pr-1 white">
            {(window as any).userAttrs.display_name || "User"}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu>
          {Object.entries(groups).map(
            ([_, items], groupIndex, groupArray) =>
              items.length > 0 && (
                <>
                  {items.map(([name, path, className]) => (
                    <Dropdown.Item
                      key={name}
                      href={typeof path === "string" ? path : undefined}
                      onClick={typeof path === "function" ? path : undefined}
                      className={className || "text-color"}
                    >
                      {name}
                    </Dropdown.Item>
                  ))}
                  {groupIndex < groupArray.length - 1 && <Dropdown.Divider />}
                </>
              )
          )}
        </Dropdown.Menu>
      </Dropdown>
    </>
  );
};

const topBarDropdownEl = document.getElementById("top-bar-dropdown");

if (topBarDropdownEl) {
  ReactDOM.createRoot(topBarDropdownEl).render(<TopBarDropdown />);
}
